import { PostProcessingResult } from '@/app/log-viewer/post-processing/PostProcessor';
import { InternalMessage } from '@/workers/InternalMessages';
import {
  WorkerClient,
  WorkerClientFactory,
  WorkerClientInput,
  WorkerClientInputTyped,
} from '@/workers/WorkerClient';
import { isCanceledError } from '@/workers/WorkerOperationState';

import {
  Reset,
  ResetArgs,
  SyncLogEntryCellChange,
  SyncLogEntryCellChangeArgs,
  SyncLogEntryRowChanges,
  SyncLogEntryRowChangesArgs,
  SyncTableModelChanges,
  SyncTableModelChangesArgs,
} from './ClientToServerOperations';
import { LogViewerTableModelDataWithRows } from './LogViewerTableModel';
import * as S2COperations from './ServerToClientOperations';

export interface LogViewerTableModelWorkerClientDelegate {
  onPostProcessStarted(): void;
  onPostProcessSucceeded(result: PostProcessingResult): void;
  onPostProcessFailed(error: any): void;
}

export interface LogViewerTableModelWorkerClient {
  delegate?: LogViewerTableModelWorkerClientDelegate;

  shutdown(): void;
  reset(data: LogViewerTableModelDataWithRows): void;
  syncTableChanges(args: SyncTableModelChangesArgs): void;
  syncLogEntryRowChanges(args: SyncLogEntryRowChangesArgs): void;
  syncLogEntryCellChange(args: SyncLogEntryCellChangeArgs): void;
}

export function makeLogViewerTableModelWorkerClient(): LogViewerTableModelWorkerClient {
  return new LogViewerTableModelWorkerClientImpl();
}

class LogViewerTableModelWorkerClientImpl implements LogViewerTableModelWorkerClient {
  private client: WorkerClient;
  delegate?: LogViewerTableModelWorkerClientDelegate;

  constructor() {
    const worker = new Worker(new URL('./LogViewerTableModelWorkerServer.ts', import.meta.url));
    this.client = WorkerClientFactory.make(worker);
    this.client.onCustomMessage = (msg) => this.handleCustomMessage(msg);
  }

  private handleCustomMessage(msg: InternalMessage) {
    console.log('WorkerClient handleCustomMessage', msg.internalName);

    if (msg.internalName === S2COperations.PostProcessingStarted) {
      this.delegate?.onPostProcessStarted();
    } else if (msg.internalName === S2COperations.PostProcessingError) {
      const ppErrorMsg = msg as S2COperations.PostProcessingErrorMessage;
      this.delegate?.onPostProcessFailed(ppErrorMsg.error);

      if (!isCanceledError(ppErrorMsg.error)) {
        console.error('PostProcessing error', ppErrorMsg.error);
      }
    } else if (msg.internalName === S2COperations.PostProcessingCompleted) {
      const ppCompletedMsg = msg as S2COperations.PostProcessingCompletedMessage;
      this.delegate?.onPostProcessSucceeded(ppCompletedMsg.result);
    } else {
      console.error(`Unknown message ${msg.internalName}`);
    }
  }

  shutdown() {
    this.client.shutdown();
  }

  reset(data: LogViewerTableModelDataWithRows): void {
    const args: WorkerClientInputTyped<ResetArgs> = {
      name: Reset,
      args: {
        data,
      },
    };
    this.client.execute(args);
  }

  syncTableChanges(args: SyncTableModelChangesArgs): void {
    const input: WorkerClientInput = {
      name: SyncTableModelChanges,
      args,
    };
    this.client.execute(input);
  }

  syncLogEntryRowChanges(args: SyncLogEntryRowChangesArgs): void {
    const input: WorkerClientInput = {
      name: SyncLogEntryRowChanges,
      args,
    };
    this.client.execute(input);
  }

  syncLogEntryCellChange(args: SyncLogEntryCellChangeArgs) {
    const input: WorkerClientInput = {
      name: SyncLogEntryCellChange,
      args,
    };
    this.client.execute(input);
  }
}
